// Demo-mode auth: no Cognito, no SMS. A visitor picks which seeded hasher to be
// (admin / misman / plain hasher) on a role-picker login screen, and we mint
// claims from the demo store on the fly so any grant they make in-session is
// reflected on the next read. Mirrors the surface of ./cognito so auth/index can
// re-export this module interchangeably.

import { getDb } from "../api/demo/store";

const IDENTITY_KEY = "hash.demo.identity.v1";

export interface DemoClaims {
  sub: string;
  hasherId: string;
  role: "admin" | "misman" | "hare";
  mismanKennels: string[];
  phone: string;
}

let selected: string | null = readSelected();

function readSelected(): string | null {
  try {
    return localStorage.getItem(IDENTITY_KEY);
  } catch {
    return null;
  }
}

/** The identities offered on the demo login screen, each with a role blurb. */
export function listIdentities(): Array<{
  hasherId: string;
  name: string;
  kennel: string;
  blurb: string;
}> {
  const db = getDb();
  const describe = (hasherId: string): string => {
    const h = db.hashers.find((x) => x.id === hasherId);
    if (h?.role === "admin") return "Admin — full access, system-wide";
    const grants = db.roleAssignments.filter((r) => r.hasherId === hasherId);
    if (grants.length > 0) {
      return `Misman of ${grants.map((g) => g.kennelName).join(", ")}`;
    }
    return "Hasher — check in, view your history";
  };
  // A curated, stable line-up that shows off each permission level.
  const ids = ["h_admin", "h_karen", "h_sticky", "h_nyc_lost"];
  return ids
    .map((id) => db.hashers.find((h) => h.id === id))
    .filter((h): h is NonNullable<typeof h> => Boolean(h))
    .map((h) => ({
      hasherId: h.id,
      name: h.name,
      kennel: h.homeKennel,
      blurb: describe(h.id),
    }));
}

export function signInAs(hasherId: string): void {
  selected = hasherId;
  try {
    localStorage.setItem(IDENTITY_KEY, hasherId);
  } catch {
    /* ignore */
  }
}

export function signOut(): void {
  selected = null;
  try {
    localStorage.removeItem(IDENTITY_KEY);
  } catch {
    /* ignore */
  }
}

export function isSignedIn(): boolean {
  return selected !== null && getDb().hashers.some((h) => h.id === selected);
}

export function getClaims(): DemoClaims | null {
  if (!selected) return null;
  const db = getDb();
  const h = db.hashers.find((x) => x.id === selected);
  if (!h) return null;
  const mismanKennels = db.roleAssignments
    .filter((r) => r.hasherId === h.id)
    .map((r) => r.kennelId);
  return {
    sub: `demo-${h.id}`,
    hasherId: h.id,
    role: h.role === "admin" ? "admin" : "hare",
    mismanKennels,
    phone: h.phone ?? "+15550000000",
  };
}

export async function getIdToken(): Promise<string | null> {
  return selected ? "demo-token" : null;
}

// --- Stubs so auth/index can re-export the full module surface. The demo login
// screen doesn't use the SMS/passkey flows, but these keep the shapes identical.

export function startSignIn(_phone: string): Promise<void> {
  return Promise.resolve();
}
export function completeSignIn(_code: string): Promise<void> {
  return Promise.resolve();
}
export function passkeySupported(): boolean {
  return false;
}
export function shouldOfferEnrollment(): boolean {
  return false;
}
export function markEnrolled(): void {}
export function markDontAsk(): void {}
export async function checkPasskeyAvailable(_phone: string): Promise<boolean> {
  return false;
}
export async function signInWithPasskey(_phone: string): Promise<void> {}
export async function enrollPasskey(): Promise<void> {}
